import { Component, OnInit, OnDestroy, Inject } from '@angular/core';

import { LoadConfigService } from '../../services/load-config.service';
import { FadeOutAnimation } from '../../animations/fade-out.animation';
import { LoadNeedsphereService } from '../../services/load-needsphere.service';
import { ArchiveDNSResultsService } from '../../services/archive-dns-results.service';
import { DecisionTransportService } from '../../services/decision-transport.service';
import { LoadStimuliService } from '../../services/load-stimuli.service';
import { StudyNavigationService } from '../../services/study-navigation.service';
import { UtilsService } from '../../services/utils.service';
import { InstructionService } from '../../services/instruction.service';

@Component({
  selector: 'app-iat-display',
  templateUrl: './iat-display.component.html',
  styleUrls: ['./iat-display.component.scss'],
  animations: FadeOutAnimation,
})
export class IATDisplayComponent implements OnInit, OnDestroy {
  showIntermission: boolean = false;
  intermissionPoints = [];

  stimuli: any[];
  keyLabels: string[];
  decisionKeys: string[];

  instructionWrongButtonPress: string;

  // initialized with true, so that the "X" is not shown when loading the page
  answerCorrect: boolean = true;
  firstAnswerCorrect: boolean = undefined;
  timeUntilFirstReaction: number = undefined;
  category: string[] = ['trust', 'links 2', 'distrust', 'rechts 2'];

  // information about block:
  blockName: string;
  blockNr: number;
  practiceMode: boolean = false;
  readingMode: boolean = false;
  brandImage = undefined;
  brandName = undefined;

  // sets the type between default, brand and agreement
  type: string = 'IAT';
  mode: string = 'IAT';

  // used to iterate through needs
  id: number = 0;
  finishedTrial: boolean = false;

  currentTime: number;
  runningTimer;

  // flag for automatically proceeding to next item in needs array
  dontProceed: boolean = true;
  isVisible: boolean = true;

  constructor(
    private loadConfigService: LoadConfigService,
    private loadNeedsphereService: LoadNeedsphereService,
    private loadStimuli: LoadStimuliService,
    private archiveDNSResultsService: ArchiveDNSResultsService,
    private decisionTransporter: DecisionTransportService,
    private navigationService: StudyNavigationService,
    private utils: UtilsService,
    private instructionService: InstructionService,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  ngOnInit(): void {
    // needed to make decision via mouse work
    this.decisionTransporter.decisionEvent.subscribe(
      (decision: number | string) => {
        this.nextItem(decision);
      }
    );

    this.getIATConfig();

    this.archiveDNSResultsService.initializeArchivedIATResults({
      mode: this.mode,
      IATtype: this.type,
      blockID: this.blockNr,
      brandImage: this.brandImage ? this.brandImage : undefined,
      brandName: this.brandName ? this.brandName : undefined,
    });

    this.setKeys(this.type);
    this.setStimuli();

    this.getIntermissionPoints();

    this.instructionWrongButtonPress = this.instructionService.getTranslationsForTerm(
      'IAT_WRONG_KEY_PRESS_INSTRUCTION'
    );
  }

  ngOnDestroy() {
    clearInterval(this.runningTimer);
  }

  getIntermissionPoints() {
    if (
      ['persona', 'brand', 'modular'].includes(this.type) &&
      !this.practiceMode &&
      !this.readingMode
    ) {
      const repetitions = this.loadNeedsphereService.getRepetitions();
      const amountOfStimuli = this.stimuli.length;

      if (repetitions >= 4) {
        const sectionStops = repetitions / 4;

        let intermissionBreaks = [];

        let i = sectionStops;
        while (i < repetitions) {
          intermissionBreaks.push(i);
          i += sectionStops;
        }

        // need to check if we went over repetitions (might be due to floating point arithmetic)
        if (intermissionBreaks[intermissionBreaks.length - 1] > repetitions) {
          intermissionBreaks = intermissionBreaks.slice(
            0,
            intermissionBreaks.length - 1
          );
        }

        this.intermissionPoints = intermissionBreaks.map(
          (point) => point * Math.ceil(amountOfStimuli / repetitions)
        );
      }
    }
  }

  startIntermission() {
    clearInterval(this.runningTimer);
    this.showIntermission = true;
  }

  stopIntermission() {
    this.showIntermission = false;
    this.startTimer();
    if (!this.dontProceed) {
      this.resetTimer();
    }
  }

  private setKeys(test: string) {
    this.decisionKeys = this.loadConfigService.getDecisionKeysIAT(test);
  }

  private setStimuli() {
    this.stimuli = this.loadStimuli.getStimuli(
      this.type,
      this.readingMode,
      this.practiceMode,
      this.mode,
      this.instructionService.getLanguageIdentifier()
    );

    this.currentTime = this.startTimer();

    if (!this.dontProceed) {
      this.resetTimer();
    }
  }

  private getIATConfig() {
    const IATConfig = this.navigationService.IATconfig;

    this.practiceMode = IATConfig.practiceMode;
    this.readingMode = IATConfig.readingMode;

    this.type = 'IAT';
    this.mode = IATConfig.mode;
    this.blockName = IATConfig.blockName;
    this.blockNr = IATConfig.blockNr;

    this.category = IATConfig.stimuliPositions;

    if (this.mode === 'reading') {
      this.dontProceed = true;
    }

    if (
      (this.mode === 'IAT' || this.mode === 'practice') &&
      IATConfig['proceedAutomatically'] === false
    ) {
      this.dontProceed = true;
    }
  }

  checkAnswerAndLoadNextItem(
    decision: number | string,
    receivedUserInput: boolean = true
  ) {
    // check if the answer is correct and update the member variable
    let correctAnswer;
    // check the categories, element 0 and 1 are on the left, 2 and 3 on the right
    if (
      this.stimuli[this.id].type == this.category[0] ||
      this.stimuli[this.id].type == this.category[1]
    ) {
      correctAnswer = 0;
    } else {
      correctAnswer = 1;
    }

    if (decision == correctAnswer) {
      if (this.firstAnswerCorrect == undefined) {
        this.firstAnswerCorrect = true;
      }

      this.answerCorrect = true;
      this.saveTime(decision, receivedUserInput);
      this.isVisible = false;

      let timer = setTimeout(() => {
        this.nextItem(decision, receivedUserInput);
        clearTimeout(timer);
      }, 300);
    } else {
      if (this.firstAnswerCorrect == undefined) {
        this.firstAnswerCorrect = false;
      }

      this.answerCorrect = false;
      this.saveTime(decision, receivedUserInput);
    }
  }

  /**
   * Shows the next item and saves the time it took for the next item to show up.
   */
  nextItem = (decision: number | string, receivedUserInput: boolean = true) => {
    this.isVisible = true;
    this.timeUntilFirstReaction = undefined;
    this.firstAnswerCorrect = undefined;

    if (this.id < this.stimuli.length) {
      this.id++;

      if (this.intermissionPoints.includes(this.id)) {
        return this.startIntermission();
      }

      this.startTimer();

      if (!this.dontProceed) {
        this.resetTimer();
      }

      // trial is finished
      if (this.id === this.stimuli.length) {
        this.finishedTrial = true;
        clearInterval(this.runningTimer);
        this.navigationService.renderNext();
      }
    } else {
      // trial is finished
      clearInterval(this.runningTimer);
      this.finishedTrial = true;
    }
  };

  private startTimer = () => {
    const time = new Date().getTime();
    this.currentTime = time;
    return time;
  };

  private endTimer = () => {
    return new Date().getTime() - this.currentTime;
  };

  private saveTime = (
    decision: number | string,
    receivedUserInput: boolean
  ) => {
    const reactionTime = this.endTimer();

    if (this.timeUntilFirstReaction == undefined) {
      this.timeUntilFirstReaction = this.endTimer();
    }

    if (this.answerCorrect == true) {
      this.archiveDNSResultsService.saveIATDecisionFoT({
        IATtype: this.type,
        mode: this.mode,
        stimulus: this.stimuli[this.id],
        reactionTimeUntilFirstDecision: this.timeUntilFirstReaction,
        reactionTimeUntilCorrectDecision: reactionTime,
        madeCorrectDecision: this.firstAnswerCorrect,
        receivedUserInput,
      });
    }
  };

  private setTimer(
    time: number,
    decision: number | string,
    receivedUserInput: boolean
  ): void {
    this.runningTimer = setInterval(() => {
      this.nextItem(decision, receivedUserInput);
    }, time);
  }

  private resetTimer = () => {
    if (this.runningTimer) {
      clearInterval(this.runningTimer);
    }
    this.setTimer(this.loadConfigService.getProceedAfter(), 0, false);
  };

  getIsImage(fileName: string) {
    return this.utils.getMediaType(fileName) === 'images';
  }

  getImageFilePath(fileName: string) {
    return `${this.baseUrl}assets/images/${fileName}`;
  }
}
